import { useState } from "react";
import api from "../api/client";
import { Key } from "lucide-react";

const APP_URL = import.meta.env.VITE_APP_URL ?? "https://joborigo.com";

export default function ConnectToken() {
  const [token, setToken] = useState(null);
  const [expires, setExpires] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const connectUrl = token ? `${APP_URL}/connect/${token}` : null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/users/me/connect-token");
      setToken(data.connect_token);
      setExpires(data.expires_at);
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(connectUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="mb-6 bg-white dark:bg-brand-dark-surface border border-gray-100 dark:border-white/10 rounded-2xl p-5">
      <div className="flex items-start gap-3">
        <Key className="w-5 h-5 flex-shrink-0 mt-0.5 text-brand-violet dark:text-brand-violet-on-dark" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Employer connect link
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-3">
            Generate a personal connect link and paste it in the{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              portfolio
            </span>{" "}
            or{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              website
            </span>{" "}
            field of your job applications — alongside your resume link. When an
            employer clicks it, they land on Joborigo and can connect their
            hiring system to send you real-time status updates.
            <span className="block mt-1 text-brand-violet dark:text-brand-violet-on-dark font-medium">
              No email address shared. Valid 72 hours. One-time use.
            </span>
          </p>

          {!token ? (
            <button
              onClick={generate}
              disabled={loading}
              className="bg-brand-violet text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
            >
              {loading ? "Generating..." : "Generate connect link"}
            </button>
          ) : (
            <div>
              {/* Full URL displayed clearly */}
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                Your connect link — paste this in your application:
              </p>
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 bg-gray-50 dark:bg-brand-dark-bg border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2.5 text-xs font-mono text-brand-violet dark:text-brand-violet-on-dark break-all">
                  {connectUrl}
                </code>
                <button
                  onClick={handleCopy}
                  className={`flex-shrink-0 text-xs px-3 py-2.5 rounded-lg border transition ${
                    copied
                      ? "border-green-300 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20"
                      : "border-brand-violet dark:border-brand-violet-on-dark text-brand-violet dark:text-brand-violet-on-dark hover:bg-brand-violet-tint dark:hover:bg-brand-violet/10"
                  }`}
                >
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              </div>

              {/* Usage tip */}
              <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-4 py-3 mb-3">
                <p className="text-xs text-brand-violet dark:text-brand-violet-on-dark leading-relaxed">
                  <span className="font-semibold">💡 How to use it:</span> Paste
                  this full link in the "Website", "Portfolio", or "Other link"
                  field when applying for jobs — alongside your Joborigo resume
                  link. Employers who click it land on a page explaining
                  Joborigo and can connect their ATS to send you status updates.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  Expires{" "}
                  {new Date(expires).toLocaleString("en-SE", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  · One-time use
                </p>
                <button
                  onClick={() => {
                    setToken(null);
                    setExpires(null);
                    generate();
                  }}
                  className="text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  Generate new
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
